/**
 * Varredura de inconsistências no fluxo URA (espelha regras de navegação da API).
 */

import { parseChatbotButtonEntries, partitionChatbotButtonEntries } from "./chatbotButtonLinks";
import { acoesFromSlot } from "./chatbotSlotActions";
import { isSlotAiResponseMode } from "./chatbotSlotFaq";
import { buildSlotPath, slotStepName } from "./chatbotNavigationActions";
import { EVENTO_INATIVIDADE, eventoFromSlot } from "./chatbotSlotEvent";
import { findInicioSlot, isGlobalExceptionSlot } from "../components/ChatbotFlow/chatbotSlotParentUtils";
import { buildChildrenByParent } from "../components/ChatbotFlow/chatbotTreeModel";
import { collectBrokenLeadActionRefs } from "./chatbotFlowAuditRefs";

export const AUDIT_SEVERITY_ERROR = "error";
export const AUDIT_SEVERITY_WARNING = "warning";
export const AUDIT_SEVERITY_INFO = "info";

export const AUDIT_RULE_MISSING_GLOBAL_EXCEPTION = "missing_global_exception";
export const AUDIT_RULE_EMPTY_GLOBAL_EXCEPTION = "empty_global_exception";
export const AUDIT_RULE_EMPTY_LOCAL_EXCEPTION = "empty_local_exception";
export const AUDIT_RULE_ORPHAN_BUTTON = "orphan_button";
export const AUDIT_RULE_ORPHAN_CHILD = "orphan_child";
export const AUDIT_RULE_DEAD_END = "dead_end";
export const AUDIT_RULE_BROKEN_GOTO = "broken_goto";
export const AUDIT_RULE_DUPLICATE_SIBLING_ID = "duplicate_sibling_id";
export const AUDIT_RULE_DUPLICATE_NOME = "duplicate_nome";
export const AUDIT_RULE_UNREACHABLE_SLOT = "unreachable_slot";
export const AUDIT_RULE_USELESS_SLOT = "useless_slot";
export const AUDIT_RULE_MENU_NO_RESPONSE = "menu_no_response";
export const AUDIT_RULE_BROKEN_AUTOMATION_REF = "broken_automation_ref";
export const AUDIT_RULE_BROKEN_LEAD_ACTION_REF = "broken_lead_action_ref";
export const AUDIT_RULE_OPEN_INPUT_IDENTIFICADOR = "open_input_identificador";
export const AUDIT_RULE_SILENT_CUSTOMER_STEP = "silent_customer_step";
export const AUDIT_RULE_MENU_OPTION_IDENTIFICADOR = "menu_option_identificador";
export const AUDIT_RULE_PASSAGEM_INCOMPLETE = "passagem_incomplete_flow";
export const AUDIT_RULE_PASSAGEM_POST_EMISSION_DEAD_END = "passagem_post_emission_dead_end";
export const AUDIT_RULE_PASSAGEM_MISSING_PASSENGER_STEP = "passagem_missing_passenger_step";
/** @deprecated use AUDIT_RULE_OPEN_INPUT_IDENTIFICADOR */
export const AUDIT_RULE_SPURIOUS_IDENTIFICADOR = AUDIT_RULE_OPEN_INPUT_IDENTIFICADOR;

export const DEFAULT_GLOBAL_EXCEPTION_RESPONSE =
  "Não entendi sua mensagem. Escolha uma das opções do menu ou digite *menu* para recomeçar.";

/** Nome já inclui sufixo (#id) deste passo — não entra mais no grupo de duplicados. */
export function slotNomeAlreadyDisambiguated(slot) {
  const nome = String(slot?.nome || "").trim();
  const id = Number(slot?.id);
  if (!nome || !Number.isFinite(id)) return false;
  return new RegExp(`\\(#${id}\\)$`).test(nome);
}

/** Gera nome único idempotente para correção de duplicate_nome. */
export function buildUniqueSlotNome(slot) {
  const id = Number(slot?.id);
  if (!Number.isFinite(id)) return String(slot?.nome || "").trim();
  const base = String(slot?.nome || "")
    .trim()
    .replace(/\s*\(#\d+\)\s*$/i, "")
    .trim();
  if (!base) return `#${id}`;
  return `${base} (#${id})`;
}

function slotNomeDuplicateKey(slot) {
  const nome = String(slot?.nome || "").trim();
  if (!nome || slotNomeAlreadyDisambiguated(slot)) return null;
  return nome.toLowerCase();
}

function slotByIdMap(slots) {
  return new Map((slots || []).map((s) => [Number(s.id), s]));
}

function identParts(identificador) {
  return String(identificador || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
}

function flowButtonsFromSlot(slot) {
  const entries = parseChatbotButtonEntries(slot?.botoes);
  return partitionChatbotButtonEntries(entries).replies.map((e) => e.flowKey).filter(Boolean);
}

function slotHasContent(slot) {
  if (!slot) return false;
  if (String(slot.resposta || "").trim()) return true;
  if (String(slot.arquivo || "").trim()) return true;
  return isSlotAiResponseMode(slot);
}

function slotHasFlowButtons(slot) {
  return flowButtonsFromSlot(slot).length > 0;
}

function slotHasNavigationAdvance(slot) {
  return acoesFromSlot(slot).some((a) => Number(a.tipo_acao) === 2 && Number(a.id_acao) > 0);
}

function slotDeliversCustomerContent(slot) {
  return slotHasContent(slot) || slotHasFlowButtons(slot);
}

function regularChildren(slotId, childrenByParent) {
  return (childrenByParent.get(String(slotId)) || []).filter(
    (c) => String(c.identificador || "").trim() !== "Exceção"
  );
}

function getGotoTargetIds(slot) {
  return acoesFromSlot(slot)
    .filter((a) => Number(a.tipo_acao) === 2 && Number(a.id_acao) > 0)
    .map((a) => Number(a.id_acao));
}

function gotoChainDeliversContent(startId, slotById, maxDepth = 25) {
  const visited = new Set();
  const queue = [Number(startId)];
  while (queue.length && visited.size < maxDepth) {
    const id = queue.shift();
    if (!Number.isFinite(id) || visited.has(id)) continue;
    visited.add(id);
    const node = slotById.get(id);
    if (!node) continue;
    if (slotDeliversCustomerContent(node) || isSlotAiResponseMode(node)) return true;
    for (const nextId of getGotoTargetIds(node)) {
      if (!visited.has(nextId)) queue.push(nextId);
    }
  }
  return false;
}

function slotHasGotoWithContent(slot, slotById) {
  return getGotoTargetIds(slot).some((id) => gotoChainDeliversContent(id, slotById));
}

function descendantDeliversContent(slotId, slotById, childrenByParent, visited = new Set(), depth = 0) {
  const id = Number(slotId);
  if (!Number.isFinite(id) || visited.has(id) || depth > 20) return false;
  visited.add(id);
  const node = slotById.get(id);
  if (!node) return false;
  if (slotDeliversCustomerContent(node) || isSlotAiResponseMode(node)) return true;
  if (slotHasGotoWithContent(node, slotById)) return true;
  return regularChildren(id, childrenByParent).some((child) =>
    descendantDeliversContent(child.id, slotById, childrenByParent, visited, depth + 1)
  );
}

function collectNamedChildIdents(children) {
  const idents = [];
  for (const child of children) {
    for (const part of identParts(child.identificador)) {
      if (!idents.some((i) => i.toLowerCase() === part.toLowerCase())) idents.push(part);
    }
  }
  return idents;
}

function findCatchAllChildWithContent(children, slotById, childrenByParent) {
  return children.find(
    (c) =>
      !identParts(c.identificador).length &&
      descendantDeliversContent(c.id, slotById, childrenByParent)
  );
}

function buildSilentBeforeNextResposta(slot, nextChild) {
  const nome = String(slot.nome || "").trim();
  const nextLabel = slotStepName(nextChild);
  const nextPreview = String(nextChild.resposta || "").trim();
  if (nextPreview) {
    const intro = nome || nextLabel || "Prosseguindo com seu atendimento.";
    if (nextPreview.startsWith(intro)) return nextPreview;
    return `${intro}\n\n${nextPreview}`;
  }
  return nome || nextLabel || "Prosseguindo com seu atendimento.";
}

function buildSilentNoGuidanceResposta(childIdents) {
  if (childIdents.length === 1) {
    return `Para continuar, digite exatamente: ${childIdents[0]}.`;
  }
  if (childIdents.length <= 6) {
    return `Para continuar, digite uma destas opções: ${childIdents.join(", ")}.`;
  }
  return `Informe sua resposta para continuar (há ${childIdents.length} caminhos nos subpassos).`;
}

function collectGotoTargetsWithoutContent(slot, slotById) {
  return getGotoTargetIds(slot)
    .filter((id) => !gotoChainDeliversContent(id, slotById))
    .map((id) => {
      const target = slotById.get(id);
      if (!target) return `passo #${id} (inexistente)`;
      const path = buildSlotPath(id, slotById);
      return path || slotStepName(target);
    });
}

function collectSilentBranchPaths(children, slotById, childrenByParent) {
  return children
    .filter((c) => !descendantDeliversContent(c.id, slotById, childrenByParent))
    .map((c) => buildSlotPath(c.id, slotById) || slotStepName(c))
    .slice(0, 8);
}

function buildSilentIssueDetails(base, extras = {}) {
  return {
    ...base,
    ...extras,
    childIdentsList: extras.childIdents?.length ? extras.childIdents.join(", ") : "",
    gotoTargetsList: extras.gotoTargets?.length ? extras.gotoTargets.join("; ") : "",
    silentBranchList: extras.silentBranches?.length ? extras.silentBranches.join("; ") : "",
  };
}

function openInputReasonLabels(reasons) {
  const labels = {
    matches_name: "igual ao nome",
    numeric: "numérico",
    short_token: "token curto",
    sequential_collection: "após coleta livre",
    open_prompt: "pergunta aberta",
  };
  return (reasons || []).map((r) => labels[r] || r).join(", ");
}

function extractMenuOptionFromNome(nome) {
  const n = String(nome || "").trim();
  if (!n.includes(" - ")) return null;
  const part = n.split(" - ")[0].trim();
  return part || null;
}

/**
 * Passos nomeados "Opção - Campo" (ex.: "Nenhuma opção - Nome") precisam identificador = opção.
 * Passos globais por inatividade não são acionados por identificador de menu.
 */
function isGlobalInactivitySlot(slot) {
  return Number(slot?.global) === 1 && eventoFromSlot(slot).tipo === EVENTO_INATIVIDADE;
}

function detectMenuOptionIdentificadorIssue(slot) {
  if (isGlobalInactivitySlot(slot)) return null;
  const nome = String(slot?.nome || "").trim();
  const menuOption = extractMenuOptionFromNome(nome);
  if (!menuOption) return null;
  if (slotHasFlowButtons(slot)) return null;
  const ident = String(slot?.identificador || "").trim();
  if (!ident || ident === "Exceção") {
    return {
      severity: AUDIT_SEVERITY_ERROR,
      reason: "missing_option_ident",
      menuOption,
      autoFix: true,
    };
  }
  if (ident.toLowerCase() === menuOption.toLowerCase()) return null;
  if (ident.toLowerCase() === nome.toLowerCase()) {
    return {
      severity: AUDIT_SEVERITY_ERROR,
      reason: "full_name_as_ident",
      menuOption,
      autoFix: true,
    };
  }
  return null;
}

/**
 * Passo sem texto/arquivo/botões visíveis: o cliente pode ficar sem feedback.
 * OK se "Ir para" encadeia até conteúdo; aviso se o próximo passo na árvore já tem texto.
 */
function detectSilentCustomerStepIssue(slot, slotById, childrenByParent) {
  if (String(slot?.identificador || "").trim() === "Exceção") return null;
  if (slotDeliversCustomerContent(slot) || isSlotAiResponseMode(slot)) return null;
  if (slotHasGotoWithContent(slot, slotById)) return null;

  const children = regularChildren(slot.id, childrenByParent);
  const hasGoto = slotHasNavigationAdvance(slot);
  const subtreeHasContent = children.some((c) =>
    descendantDeliversContent(c.id, slotById, childrenByParent)
  );

  if (hasGoto && !subtreeHasContent) {
    const gotoTargets = collectGotoTargetsWithoutContent(slot, slotById);
    return buildSilentIssueDetails(
      {
        severity: AUDIT_SEVERITY_ERROR,
        reason: "goto_without_message",
        autoFix: false,
        gotoTargets,
      },
      { gotoTargets }
    );
  }

  if (!children.length) return null;

  if (slotHasFlowButtons(slot)) return null;

  if (!subtreeHasContent) {
    const silentBranches = collectSilentBranchPaths(children, slotById, childrenByParent);
    return buildSilentIssueDetails(
      {
        severity: AUDIT_SEVERITY_ERROR,
        reason: "branch_without_message",
        autoFix: false,
        silentBranches,
      },
      { silentBranches }
    );
  }

  const catchAllChild = findCatchAllChildWithContent(children, slotById, childrenByParent);
  if (catchAllChild) {
    const nextStepName = slotStepName(catchAllChild);
    return buildSilentIssueDetails(
      {
        severity: AUDIT_SEVERITY_WARNING,
        reason: "silent_before_next",
        autoFix: true,
        resposta: buildSilentBeforeNextResposta(slot, catchAllChild),
        nextStepName,
      },
      { nextStepName }
    );
  }

  const childIdents = collectNamedChildIdents(children);
  return buildSilentIssueDetails(
    {
      severity: AUDIT_SEVERITY_ERROR,
      reason: "silent_no_guidance",
      autoFix: true,
      resposta: buildSilentNoGuidanceResposta(childIdents),
      childIdents,
    },
    { childIdents }
  );
}

function parentHasFlowButtonForIdent(parentSlot, ident) {
  if (!parentSlot || !ident) return false;
  const keys = flowButtonsFromSlot(parentSlot);
  const parts = identParts(ident);
  return keys.some((k) => parts.some((p) => p.toLowerCase() === String(k).toLowerCase()));
}

function isParentOpenInputStep(parentSlot) {
  if (!parentSlot) return false;
  if (slotHasFlowButtons(parentSlot)) return false;
  if (Number(parentSlot.global) === 1) return false;
  if (String(parentSlot.identificador || "").trim() === "Exceção") return false;
  if (!identParts(parentSlot.identificador).length) return true;
  return !!(String(parentSlot.resposta || "").trim() || String(parentSlot.arquivo || "").trim());
}

function isOpenPromptStep(slot) {
  if (slotHasFlowButtons(slot)) return false;
  if (Number(slot?.global) === 1) return false;
  return !!(String(slot.resposta || "").trim() || String(slot.arquivo || "").trim());
}

/**
 * Passos de coleta livre devem ter identificador vazio (URA aceita qualquer resposta).
 * Identificador preenchido pode ser intencional, mas é suspeito — risco maior se numérico,
 * igual ao nome ou filho de passo que já aceita texto livre.
 */
function detectOpenInputIdentificadorIssue(slot, parentSlot) {
  const ident = String(slot?.identificador || "").trim();
  if (!ident || ident === "Exceção" || ident === "Início") return null;
  if (extractMenuOptionFromNome(String(slot?.nome || ""))) return null;
  if (!isOpenPromptStep(slot)) return null;
  if (parentHasFlowButtonForIdent(parentSlot, ident)) return null;

  const nome = String(slot?.nome || "").trim();
  const reasons = [];

  if (nome && ident.toLowerCase() === nome.toLowerCase()) {
    reasons.push("matches_name");
  }
  if (/^\d+$/.test(ident)) {
    reasons.push("numeric");
  }
  if (identParts(ident).length === 1 && !/\s/.test(ident) && ident.length <= 12) {
    reasons.push("short_token");
  }
  if (isParentOpenInputStep(parentSlot)) {
    reasons.push("sequential_collection");
  }
  if (!reasons.length) {
    reasons.push("open_prompt");
  }

  const highConfidence =
    reasons.includes("matches_name") ||
    reasons.includes("numeric") ||
    reasons.includes("sequential_collection");

  return {
    identificador: ident,
    nome,
    reasons,
    reasonLabels: openInputReasonLabels(reasons),
    severity: highConfidence ? AUDIT_SEVERITY_ERROR : AUDIT_SEVERITY_WARNING,
    autoFix: highConfidence,
  };
}

function exceptionIsConfigured(slot) {
  if (!slot) return false;
  return slotHasContent(slot) || slotHasFlowButtons(slot);
}

function findGlobalException(slots, inicio) {
  if (!inicio) return null;
  return (slots || []).find(
    (s) =>
      Number(s.id_pai) === Number(inicio.id) && String(s.identificador || "").trim() === "Exceção"
  );
}

function findLocalException(slots, parentId) {
  return (slots || []).find(
    (s) =>
      Number(s.id_pai) === Number(parentId) && String(s.identificador || "").trim() === "Exceção"
  );
}

function hasEffectiveException(slots, parentId, globalException) {
  const local = findLocalException(slots, parentId);
  if (local && exceptionIsConfigured(local)) return true;
  return !!(globalException && exceptionIsConfigured(globalException));
}

function childMatchesButton(child, flowKey) {
  return identParts(child.identificador).includes(flowKey);
}

function makeIssue({
  rule,
  severity,
  slot,
  slots,
  slotById,
  details = {},
  fix = null,
  issueKey,
}) {
  const slotId = slot?.id != null ? Number(slot.id) : null;
  const slotPath = slot ? buildSlotPath(slot.id, slotById) : details.slotPath || "";
  const slotLabel = slot ? slotStepName(slot) : details.slotLabel || "";
  return {
    id: issueKey || `${rule}:${slotId ?? "root"}:${JSON.stringify(details)}`,
    rule,
    severity,
    slotId,
    slotPath,
    slotLabel,
    details,
    fix,
    autoFixable: !!fix,
  };
}

function computeReachableSlotIds(slots, slotById, childrenByParent, inicio) {
  const reachable = new Set();
  const queue = [];

  if (inicio) queue.push(Number(inicio.id));
  for (const s of slots || []) {
    if (Number(s.global) === 1) queue.push(Number(s.id));
  }

  while (queue.length) {
    const id = queue.shift();
    if (!Number.isFinite(id) || reachable.has(id)) continue;
    reachable.add(id);
    const slot = slotById.get(id);
    if (!slot) continue;

    for (const child of childrenByParent.get(String(id)) || []) {
      queue.push(Number(child.id));
    }

    for (const action of acoesFromSlot(slot)) {
      if (Number(action.tipo_acao) === 2 && Number(action.id_acao) > 0) {
        queue.push(Number(action.id_acao));
      }
    }
  }

  return reachable;
}

function isUselessEmptySlot(slot, childrenByParent) {
  if (!slot) return false;
  const ident = String(slot.identificador || "").trim();
  if (ident === "Início" || ident === "Exceção") return false;
  if (slotHasContent(slot) || slotHasFlowButtons(slot) || slotHasNavigationAdvance(slot)) return false;
  if (isSlotAiResponseMode(slot)) return false;
  const childCount = (childrenByParent.get(String(slot.id)) || []).length;
  return childCount === 0;
}

function slotTicketActionIds(slot) {
  return acoesFromSlot(slot)
    .filter((a) => Number(a.tipo_acao) === 1)
    .map((a) => Number(a.id_acao))
    .filter(Number.isFinite);
}

const PASSAGEM_CONSULT_IDS = new Set([5, 13, 20, 73, 51]);
const PASSAGEM_SEAT_IDS = new Set([8, 14, 19, 53]);
const PASSAGEM_PRERESERVA_IDS = new Set([68]);
const PASSAGEM_EMIT_IDS = new Set([12, 37, 40, 41, 42, 43, 44, 45]);
const PASSAGEM_PASSENGER_FIELD_IDS = new Set([10, 11]);

function collectPassagemActionIds(slots) {
  const ids = new Set();
  for (const slot of slots || []) {
    for (const id of slotTicketActionIds(slot)) ids.add(id);
  }
  return ids;
}

function slotHasPostEmissionAdvance(slot, childrenByParent) {
  if (slotHasFlowButtons(slot) || slotHasNavigationAdvance(slot)) return true;
  const children = regularChildren(slot.id, childrenByParent);
  return children.some((c) => slotDeliversCustomerContent(c) || slotHasNavigationAdvance(c));
}

function auditPassagemFlowIssues({ slots, slotById, childrenByParent }) {
  const list = Array.isArray(slots) ? slots : [];
  const actionIds = collectPassagemActionIds(list);
  if (!actionIds.size) return [];

  const hasConsult = [...actionIds].some((id) => PASSAGEM_CONSULT_IDS.has(id));
  const hasSeats = [...actionIds].some((id) => PASSAGEM_SEAT_IDS.has(id));
  const hasPre = [...actionIds].some((id) => PASSAGEM_PRERESERVA_IDS.has(id));
  const hasEmit = [...actionIds].some((id) => PASSAGEM_EMIT_IDS.has(id));
  const hasPassengerFields = [...actionIds].some((id) => PASSAGEM_PASSENGER_FIELD_IDS.has(id));

  const out = [];
  const ticketSlots = list.filter((s) => slotTicketActionIds(s).length > 0);
  const anchor = ticketSlots[0] || list[0];
  if (!anchor) return out;

  if ((hasPre || hasEmit) && (!hasConsult || !hasSeats)) {
    out.push(
      makeIssue({
        rule: AUDIT_RULE_PASSAGEM_INCOMPLETE,
        severity: AUDIT_SEVERITY_WARNING,
        slot: anchor,
        slots: list,
        slotById,
        details: { hasConsult, hasSeats, hasPre, hasEmit },
        issueKey: AUDIT_RULE_PASSAGEM_INCOMPLETE,
        fix: null,
      })
    );
  }

  if ((hasPre || hasEmit) && !hasPassengerFields) {
    out.push(
      makeIssue({
        rule: AUDIT_RULE_PASSAGEM_MISSING_PASSENGER_STEP,
        severity: AUDIT_SEVERITY_INFO,
        slot: anchor,
        slots: list,
        slotById,
        details: { hint: "Inclua coleta de nome/documento antes da pré-reserva ou emissão." },
        issueKey: AUDIT_RULE_PASSAGEM_MISSING_PASSENGER_STEP,
        fix: null,
      })
    );
  }

  for (const slot of list) {
    const ids = slotTicketActionIds(slot);
    if (!ids.some((id) => PASSAGEM_EMIT_IDS.has(id))) continue;
    if (slotHasPostEmissionAdvance(slot, childrenByParent)) continue;
    out.push(
      makeIssue({
        rule: AUDIT_RULE_PASSAGEM_POST_EMISSION_DEAD_END,
        severity: AUDIT_SEVERITY_INFO,
        slot,
        slots: list,
        slotById,
        details: { hint: "Após emitir BPe, ofereça encerramento ou menu principal." },
        issueKey: `${AUDIT_RULE_PASSAGEM_POST_EMISSION_DEAD_END}:${slot.id}`,
        fix: null,
      })
    );
  }

  return out;
}

/**
 * @param {{ slots: object[], actionsCatalog?: object[], entityIndex?: object, parameterForm?: object }} params
 * @returns {{ issues: object[], summary: object }}
 */
export function auditChatbotFlow({ slots = [], actionsCatalog = [], entityIndex = null, parameterForm = null } = {}) {
  const list = Array.isArray(slots) ? slots : [];
  const issues = [];
  const slotById = slotByIdMap(list);
  const childrenByParent = buildChildrenByParent(list);
  const inicio = findInicioSlot(list);
  const globalException = findGlobalException(list, inicio);
  const reachable = computeReachableSlotIds(list, slotById, childrenByParent, inicio);
  const slotIds = new Set(list.map((s) => Number(s.id)).filter(Number.isFinite));

  const automationIds = new Set(
    (actionsCatalog || [])
      .filter((a) => Number(a.tipo_acao) === 3)
      .map((a) => Number(a.id_acao))
      .filter(Number.isFinite)
  );

  let leadActionForm = parameterForm;
  if (!leadActionForm) {
    const catalog = (actionsCatalog || []).find((a) => Number(a.tipo_acao) === 4 && Number(a.id_acao) === 4);
    leadActionForm = catalog?.parameterForm || null;
  }

  if (inicio && !globalException) {
    issues.push(
      makeIssue({
        rule: AUDIT_RULE_MISSING_GLOBAL_EXCEPTION,
        severity: AUDIT_SEVERITY_WARNING,
        slot: inicio,
        slots: list,
        slotById,
        details: {},
        issueKey: AUDIT_RULE_MISSING_GLOBAL_EXCEPTION,
        fix: {
          kind: "create_slot",
          payload: {
            parent_id: Number(inicio.id),
            identificador: "Exceção",
            nome: "",
            resposta: DEFAULT_GLOBAL_EXCEPTION_RESPONSE,
            criar_excecao: false,
            global: 0,
            ignorarglobal: 0,
          },
        },
      })
    );
  }

  if (globalException && !exceptionIsConfigured(globalException)) {
    issues.push(
      makeIssue({
        rule: AUDIT_RULE_EMPTY_GLOBAL_EXCEPTION,
        severity: AUDIT_SEVERITY_WARNING,
        slot: globalException,
        slots: list,
        slotById,
        details: {},
        issueKey: AUDIT_RULE_EMPTY_GLOBAL_EXCEPTION,
        fix: {
          kind: "update_slot",
          payload: {
            id: Number(globalException.id),
            resposta: DEFAULT_GLOBAL_EXCEPTION_RESPONSE,
          },
        },
      })
    );
  }

  const globalUsable = !!(globalException && exceptionIsConfigured(globalException));
  for (const slot of list) {
    if (String(slot.identificador || "").trim() !== "Exceção") continue;
    if (isGlobalExceptionSlot(slot, list)) continue;
    if (exceptionIsConfigured(slot)) continue;

    const parent = slot.id_pai ? slotById.get(Number(slot.id_pai)) : null;
    issues.push(
      makeIssue({
        rule: AUDIT_RULE_EMPTY_LOCAL_EXCEPTION,
        severity: AUDIT_SEVERITY_WARNING,
        slot,
        slots: list,
        slotById,
        details: { parentPath: parent ? buildSlotPath(parent.id, slotById) : "" },
        issueKey: `${AUDIT_RULE_EMPTY_LOCAL_EXCEPTION}:${slot.id}`,
        fix: globalUsable
          ? { kind: "delete_slot", payload: { id: Number(slot.id) } }
          : null,
      })
    );
  }

  const nomeGroups = new Map();
  for (const slot of list) {
    const key = slotNomeDuplicateKey(slot);
    if (!key) continue;
    if (!nomeGroups.has(key)) nomeGroups.set(key, []);
    nomeGroups.get(key).push(slot);
  }
  for (const [key, group] of nomeGroups.entries()) {
    if (group.length <= 1) continue;
    for (const slot of group) {
      issues.push(
        makeIssue({
          rule: AUDIT_RULE_DUPLICATE_NOME,
          severity: AUDIT_SEVERITY_WARNING,
          slot,
          slots: list,
          slotById,
          details: {
            nome: group[0].nome,
            slotId: Number(slot.id),
            duplicateIds: group.map((s) => Number(s.id)),
          },
          issueKey: `${AUDIT_RULE_DUPLICATE_NOME}:${slot.id}:${key}`,
          fix: {
            kind: "update_slot",
            payload: {
              id: Number(slot.id),
              nome: buildUniqueSlotNome(slot),
            },
          },
        })
      );
    }
  }

  for (const slot of list) {
    const ident = String(slot.identificador || "").trim();
    if (ident === "Exceção") continue;

    const parentSlot = slot.id_pai ? slotById.get(Number(slot.id_pai)) : null;
    const openInputIssue = detectOpenInputIdentificadorIssue(slot, parentSlot);
    if (openInputIssue) {
      issues.push(
        makeIssue({
          rule: AUDIT_RULE_OPEN_INPUT_IDENTIFICADOR,
          severity: openInputIssue.severity,
          slot,
          slots: list,
          slotById,
          details: {
            identificador: openInputIssue.identificador,
            nome: openInputIssue.nome,
            reasons: openInputIssue.reasons,
            reasonLabels: openInputIssue.reasonLabels,
          },
          issueKey: `${AUDIT_RULE_OPEN_INPUT_IDENTIFICADOR}:${slot.id}:${openInputIssue.reasons.join(",")}`,
          fix: openInputIssue.autoFix
            ? {
                kind: "update_slot",
                payload: {
                  id: Number(slot.id),
                  identificador: "",
                },
              }
            : null,
        })
      );
    }

    const menuOptionIssue = detectMenuOptionIdentificadorIssue(slot);
    if (menuOptionIssue) {
      issues.push(
        makeIssue({
          rule: AUDIT_RULE_MENU_OPTION_IDENTIFICADOR,
          severity: menuOptionIssue.severity,
          slot,
          slots: list,
          slotById,
          details: {
            reason: menuOptionIssue.reason,
            menuOption: menuOptionIssue.menuOption,
            nome: String(slot.nome || "").trim(),
          },
          issueKey: `${AUDIT_RULE_MENU_OPTION_IDENTIFICADOR}:${slot.id}:${menuOptionIssue.reason}`,
          fix: menuOptionIssue.autoFix
            ? {
                kind: "update_slot",
                payload: {
                  id: Number(slot.id),
                  identificador: menuOptionIssue.menuOption,
                },
              }
            : null,
        })
      );
    }

    const flowButtons = flowButtonsFromSlot(slot);
    if (flowButtons.length) {
      const children = (childrenByParent.get(String(slot.id)) || []).filter(
        (c) => String(c.identificador || "").trim() !== "Exceção"
      );

      for (const flowKey of flowButtons) {
        const hasChild = children.some((child) => childMatchesButton(child, flowKey));
        if (!hasChild) {
          issues.push(
            makeIssue({
              rule: AUDIT_RULE_ORPHAN_BUTTON,
              severity: AUDIT_SEVERITY_ERROR,
              slot,
              slots: list,
              slotById,
              details: { buttonLabel: flowKey },
              issueKey: `${AUDIT_RULE_ORPHAN_BUTTON}:${slot.id}:${flowKey}`,
              fix: {
                kind: "create_slot",
                payload: {
                  parent_id: Number(slot.id),
                  identificador: flowKey,
                  criar_excecao: false,
                  global: 0,
                  ignorarglobal: 0,
                },
              },
            })
          );
        }
      }

      if (!String(slot.resposta || "").trim() && !isSlotAiResponseMode(slot)) {
        issues.push(
          makeIssue({
            rule: AUDIT_RULE_MENU_NO_RESPONSE,
            severity: AUDIT_SEVERITY_INFO,
            slot,
            slots: list,
            slotById,
            details: {},
            issueKey: `${AUDIT_RULE_MENU_NO_RESPONSE}:${slot.id}`,
            fix: {
              kind: "update_slot",
              payload: {
                id: Number(slot.id),
                resposta: "Escolha uma das opções abaixo:",
              },
            },
          })
        );
      }
    }

    const children = childrenByParent.get(String(slot.id)) || [];
    const parentFlowKeys = new Set(flowButtons);
    for (const child of children) {
      if (String(child.identificador || "").trim() === "Exceção") continue;
      const parts = identParts(child.identificador);
      if (!parts.length) continue;
      const matchesButton = parts.some((p) => parentFlowKeys.has(p));
      if (!matchesButton && parentFlowKeys.size > 0) {
        issues.push(
          makeIssue({
            rule: AUDIT_RULE_ORPHAN_CHILD,
            severity: AUDIT_SEVERITY_WARNING,
            slot: child,
            slots: list,
            slotById,
            details: {
              identificador: parts.join(", "),
              parentPath: buildSlotPath(slot.id, slotById),
            },
            issueKey: `${AUDIT_RULE_ORPHAN_CHILD}:${child.id}`,
            fix: isUselessEmptySlot(child, childrenByParent)
              ? { kind: "delete_slot", payload: { id: Number(child.id) } }
              : null,
          })
        );
      }
    }

    const siblingGroups = new Map();
    for (const child of children) {
      if (String(child.identificador || "").trim() === "Exceção") continue;
      for (const part of identParts(child.identificador)) {
        const key = part.toLowerCase();
        if (!siblingGroups.has(key)) siblingGroups.set(key, []);
        siblingGroups.get(key).push(child);
      }
    }
    for (const [key, group] of siblingGroups.entries()) {
      const uniqueIds = [...new Set(group.map((c) => Number(c.id)))];
      if (uniqueIds.length <= 1) continue;
      for (let i = 1; i < uniqueIds.length; i += 1) {
        const dup = slotById.get(uniqueIds[i]);
        if (!dup) continue;
        const newIdent = `${identParts(dup.identificador)[0] || key}_${dup.id}`;
        issues.push(
          makeIssue({
            rule: AUDIT_RULE_DUPLICATE_SIBLING_ID,
            severity: AUDIT_SEVERITY_WARNING,
            slot: dup,
            slots: list,
            slotById,
            details: { duplicateKey: key, suggestedIdentificador: newIdent },
            issueKey: `${AUDIT_RULE_DUPLICATE_SIBLING_ID}:${slot.id}:${dup.id}:${key}`,
            fix: {
              kind: "update_slot",
              payload: { id: Number(dup.id), identificador: newIdent },
            },
          })
        );
      }
    }

    for (const action of acoesFromSlot(slot)) {
      if (Number(action.tipo_acao) !== 2) continue;
      const targetId = Number(action.id_acao);
      if (!targetId || slotIds.has(targetId)) continue;
      const remaining = acoesFromSlot(slot).filter(
        (a) => !(Number(a.tipo_acao) === 2 && Number(a.id_acao) === targetId)
      );
      issues.push(
        makeIssue({
          rule: AUDIT_RULE_BROKEN_GOTO,
          severity: AUDIT_SEVERITY_ERROR,
          slot,
          slots: list,
          slotById,
          details: { targetId },
          issueKey: `${AUDIT_RULE_BROKEN_GOTO}:${slot.id}:${targetId}`,
          fix: {
            kind: "update_slot",
            payload: {
              id: Number(slot.id),
              acoes: remaining,
              ...legacyFromAcoes(remaining),
            },
          },
        })
      );
    }

    for (const action of acoesFromSlot(slot)) {
      if (Number(action.tipo_acao) !== 3) continue;
      const ruleId = Number(action.id_acao);
      if (!ruleId || automationIds.size === 0) continue;
      if (automationIds.has(ruleId)) continue;
      issues.push(
        makeIssue({
          rule: AUDIT_RULE_BROKEN_AUTOMATION_REF,
          severity: AUDIT_SEVERITY_WARNING,
          slot,
          slots: list,
          slotById,
          details: { ruleId },
          issueKey: `${AUDIT_RULE_BROKEN_AUTOMATION_REF}:${slot.id}:${ruleId}`,
          fix: null,
        })
      );
    }

    if (entityIndex && leadActionForm) {
      for (const broken of collectBrokenLeadActionRefs(slot, leadActionForm, entityIndex)) {
        issues.push(
          makeIssue({
            rule: AUDIT_RULE_BROKEN_LEAD_ACTION_REF,
            severity: AUDIT_SEVERITY_WARNING,
            slot,
            slots: list,
            slotById,
            details: {
              refKind: broken.refKind,
              refId: broken.refId,
              field: broken.field,
              label: broken.label,
            },
            issueKey: `${AUDIT_RULE_BROKEN_LEAD_ACTION_REF}:${slot.id}:${broken.actionIndex}:${broken.field}:${broken.refId}`,
            fix: broken.fix,
          })
        );
      }
    }

    if (ident === "Início") continue;
    if (isSlotAiResponseMode(slot)) continue;

    if (reachable.has(Number(slot.id))) {
      const silentIssue = detectSilentCustomerStepIssue(slot, slotById, childrenByParent);
      if (silentIssue) {
        issues.push(
          makeIssue({
            rule: AUDIT_RULE_SILENT_CUSTOMER_STEP,
            severity: silentIssue.severity,
            slot,
            slots: list,
            slotById,
            details: {
              reason: silentIssue.reason,
              nextStepName: silentIssue.nextStepName || "",
              childIdentsList: silentIssue.childIdentsList || "",
              gotoTargetsList: silentIssue.gotoTargetsList || "",
              silentBranchList: silentIssue.silentBranchList || "",
            },
            issueKey: `${AUDIT_RULE_SILENT_CUSTOMER_STEP}:${slot.id}:${silentIssue.reason}`,
            fix: silentIssue.autoFix
              ? {
                  kind: "update_slot",
                  payload: {
                    id: Number(slot.id),
                    resposta: silentIssue.resposta,
                  },
                }
              : null,
          })
        );
      }
    }

    if (slotHasNavigationAdvance(slot)) continue;

    const nonExceptionChildren = children.filter(
      (c) => String(c.identificador || "").trim() !== "Exceção"
    );
    const hasCatchAllChild = nonExceptionChildren.some((c) => !identParts(c.identificador).length);

    if (
      reachable.has(Number(slot.id)) &&
      !slotHasFlowButtons(slot) &&
      nonExceptionChildren.length === 0 &&
      !hasCatchAllChild &&
      !hasEffectiveException(list, slot.id, globalException)
    ) {
      issues.push(
        makeIssue({
          rule: AUDIT_RULE_DEAD_END,
          severity: AUDIT_SEVERITY_ERROR,
          slot,
          slots: list,
          slotById,
          details: {},
          issueKey: `${AUDIT_RULE_DEAD_END}:${slot.id}`,
          fix:
            inicio && Number(slot.id) !== Number(inicio.id)
              ? {
                  kind: "update_slot",
                  payload: buildGotoInicioFix(slot, inicio),
                }
              : globalException
                ? {
                    kind: "update_slot",
                    payload: {
                      id: Number(globalException.id),
                      resposta: DEFAULT_GLOBAL_EXCEPTION_RESPONSE,
                    },
                  }
                : {
                    kind: "create_slot",
                    payload: {
                      parent_id: Number(inicio?.id || 0),
                      identificador: "Exceção",
                      resposta: DEFAULT_GLOBAL_EXCEPTION_RESPONSE,
                      criar_excecao: false,
                      global: 0,
                      ignorarglobal: 0,
                    },
                  },
        })
      );
    }
  }

  for (const slot of list) {
    const ident = String(slot.identificador || "").trim();
    if (ident === "Início") continue;
    if (!reachable.has(Number(slot.id))) {
      issues.push(
        makeIssue({
          rule: AUDIT_RULE_UNREACHABLE_SLOT,
          severity: AUDIT_SEVERITY_INFO,
          slot,
          slots: list,
          slotById,
          details: {},
          issueKey: `${AUDIT_RULE_UNREACHABLE_SLOT}:${slot.id}`,
          fix: isUselessEmptySlot(slot, childrenByParent)
            ? { kind: "delete_slot", payload: { id: Number(slot.id) } }
            : null,
        })
      );
    }

    if (isUselessEmptySlot(slot, childrenByParent)) {
      issues.push(
        makeIssue({
          rule: AUDIT_RULE_USELESS_SLOT,
          severity: AUDIT_SEVERITY_INFO,
          slot,
          slots: list,
          slotById,
          details: {},
          issueKey: `${AUDIT_RULE_USELESS_SLOT}:${slot.id}`,
          fix: { kind: "delete_slot", payload: { id: Number(slot.id) } },
        })
      );
    }
  }

  issues.push(...auditPassagemFlowIssues({ slots: list, slotById, childrenByParent }));

  const deduped = dedupeIssues(issues);
  const summary = {
    total: deduped.length,
    errors: deduped.filter((i) => i.severity === AUDIT_SEVERITY_ERROR).length,
    warnings: deduped.filter((i) => i.severity === AUDIT_SEVERITY_WARNING).length,
    info: deduped.filter((i) => i.severity === AUDIT_SEVERITY_INFO).length,
    autoFixable: deduped.filter((i) => i.autoFixable).length,
  };

  return { issues: deduped, summary };
}

function dedupeIssues(issues) {
  const seen = new Set();
  const out = [];
  for (const issue of issues) {
    if (seen.has(issue.id)) continue;
    seen.add(issue.id);
    out.push(issue);
  }
  return out;
}

function legacyFromAcoes(acoes) {
  const first = acoes?.[0];
  if (!first) return { tipo_acao: 0, id_acao: 0 };
  return {
    tipo_acao: Number(first.tipo_acao) || 0,
    id_acao: Number(first.id_acao) || 0,
  };
}

function buildGotoInicioFix(slot, inicio) {
  const existing = acoesFromSlot(slot);
  const hasGoto = existing.some(
    (a) => Number(a.tipo_acao) === 2 && Number(a.id_acao) === Number(inicio.id)
  );
  if (hasGoto) {
    return { id: Number(slot.id) };
  }
  const acoes = [
    ...existing,
    {
      tipo_acao: 2,
      id_acao: Number(inicio.id),
      values: {},
      labels: {},
    },
  ];
  return {
    id: Number(slot.id),
    acoes,
    ...legacyFromAcoes(acoes),
  };
}

/** Limite do endpoint POST /chatbot/slots/audit/apply (deve coincidir com MAX_BATCH_FIXES na API). */
export const AUDIT_BATCH_FIX_LIMIT = 80;

export function getAuditBatchPlan(fixCount, limit = AUDIT_BATCH_FIX_LIMIT) {
  const count = Math.max(0, Number(fixCount) || 0);
  if (!count) {
    return { count: 0, limit, batches: 0 };
  }
  return { count, limit, batches: Math.ceil(count / limit) };
}

/**
 * Aplica correções selecionadas — usa applyBatchFn quando fornecido (lote transacional no backend).
 * Acima de AUDIT_BATCH_FIX_LIMIT, envia vários lotes sequenciais automaticamente.
 * @param {object[]} issues - issues com fix definido
 * @param {{ createSlot: object, updateSlot: object, deleteSlot: object, userId: number, applyBatchFn?: Function, onBatchProgress?: Function }} deps
 */
export async function applyChatbotFlowFixes(issues, {
  createSlot,
  updateSlot,
  deleteSlot,
  userId,
  applyBatchFn,
  onBatchProgress,
}) {
  const fixable = (issues || []).filter((i) => i?.fix);
  if (!fixable.length) {
    return { applied: [], failed: [], batch: false, batches: 0, batchErrors: [] };
  }

  if (typeof applyBatchFn === "function" && userId) {
    const totalBatches = Math.ceil(fixable.length / AUDIT_BATCH_FIX_LIMIT);

    const runBatch = async (chunk, batchIndex) => {
      onBatchProgress?.({ batch: batchIndex, totalBatches, appliedSoFar: 0 });
      const result = await applyBatchFn(chunk, userId);
      return {
        applied: Array.isArray(result?.applied) ? result.applied : [],
        failed: Array.isArray(result?.failed) ? result.failed : [],
        batch: true,
      };
    };

    if (fixable.length <= AUDIT_BATCH_FIX_LIMIT) {
      const result = await runBatch(fixable, 1);
      return { ...result, batches: 1, batchErrors: [] };
    }

    const applied = [];
    const failed = [];
    const batchErrors = [];

    for (let offset = 0; offset < fixable.length; offset += AUDIT_BATCH_FIX_LIMIT) {
      const batchIndex = Math.floor(offset / AUDIT_BATCH_FIX_LIMIT) + 1;
      const chunk = fixable.slice(offset, offset + AUDIT_BATCH_FIX_LIMIT);
      onBatchProgress?.({ batch: batchIndex, totalBatches, appliedSoFar: applied.length });

      try {
        const result = await runBatch(chunk, batchIndex);
        applied.push(...result.applied);
        failed.push(...result.failed);
      } catch (err) {
        batchErrors.push({
          batch: batchIndex,
          message: err?.message || String(err),
        });
        break;
      }
    }

    return {
      applied,
      failed,
      batch: true,
      batches: totalBatches,
      batchErrors,
      partial: batchErrors.length > 0,
    };
  }

  return applyChatbotFlowFixesSequential(fixable, { createSlot, updateSlot, deleteSlot, userId });
}

async function applyChatbotFlowFixesSequential(issues, { createSlot, updateSlot, deleteSlot, userId }) {
  const applied = [];
  const failed = [];

  for (const issue of issues) {
    if (!issue?.fix) continue;
    try {
      const { kind, payload } = issue.fix;
      if (kind === "create_slot") {
        await createSlot.mutateAsync({ ...payload, user_id: userId });
      } else if (kind === "update_slot") {
        await updateSlot.mutateAsync(payload);
      } else if (kind === "delete_slot") {
        await deleteSlot.mutateAsync(payload.id);
      } else {
        throw new Error(`unknown_fix_kind:${kind}`);
      }
      applied.push(issue.id);
    } catch (err) {
      failed.push({ issueId: issue.id, message: err?.message || String(err) });
    }
  }

  return { applied, failed, batch: false };
}

export function groupIssuesByRule(issues) {
  const map = new Map();
  for (const issue of issues || []) {
    map.set(issue.rule, (map.get(issue.rule) || 0) + 1);
  }
  return map;
}
